// #294 redesign — the super admin's critical sound rules (Settings → System → Critical alert sound), one
// setting for every dashboard: which sound, how often it repeats, when it stops repeating. Every open
// dashboard re-reads them each minute (background tabs too, they play the sound). One that cannot read
// them — an old API without the endpoint (404), a network error — uses the defaults; while they are still
// loading the popups and the sound wait (`settled`).
import { useQuery } from '@tanstack/react-query'
import { systemSettingsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { useAuthStore } from '@/store/authStore'
import { DEFAULT_CRITICAL_SOUND_SETTINGS, type CriticalSoundSettings } from '@/types'

// #338 — the client's recording and a 5 s repeat (the server's choices, views.py CRITICAL_SOUND_CHOICES)
const SOUNDS: ReadonlyArray<CriticalSoundSettings['sound']> = ['client', 'hazard', 'siren', 'horn']
const REPEATS: ReadonlyArray<CriticalSoundSettings['repeat_seconds']> = [0, 5, 10, 30, 60]
const STOPS: ReadonlyArray<CriticalSoundSettings['stop_after_seconds']> = [60, 300, 600, 0]

function pick<T>(allowed: ReadonlyArray<T>, value: unknown, fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback
}

/** The rules as the sound engine may use them: a value outside the choices (bad data, a newer API with a
 *  choice this dashboard does not know) reads as that key's default. Module level: a stable select. */
function choicesOnly(data: CriticalSoundSettings): CriticalSoundSettings {
  const d = DEFAULT_CRITICAL_SOUND_SETTINGS
  const raw = (data ?? {}) as Partial<Record<keyof CriticalSoundSettings, unknown>>
  return {
    sound: pick(SOUNDS, raw.sound, d.sound),
    repeat_seconds: pick(REPEATS, raw.repeat_seconds, d.repeat_seconds),
    stop_after_seconds: pick(STOPS, raw.stop_after_seconds, d.stop_after_seconds),
  }
}

export interface CriticalSoundSettingsState {
  /** The rules, or the defaults while loading, on error and on an old API. */
  settings: CriticalSoundSettings
  /** The request has finished (read, or failed: old API 404, network error). Until then the popups and the
   *  sound wait (review 2026-10-12): the defaults must not decide for an alert the real rules have ended. */
  settled: boolean
}

/** The super admin's critical sound rules and whether they have been read (or failed) yet. */
export function useCriticalSoundSettingsState(): CriticalSoundSettingsState {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { data, status } = useQuery({
    queryKey: queryKeys.systemSettings.criticalSound(),
    queryFn: () => systemSettingsApi.getCriticalSound(),
    select: choicesOnly,
    enabled: isAuthenticated,
    ...queryOptions.systemSettings.criticalSound,
  })
  return { settings: data ?? DEFAULT_CRITICAL_SOUND_SETTINGS, settled: status !== 'pending' }
}

/** The super admin's critical sound rules, or the defaults while loading, on error and on an old API. */
export function useCriticalSoundSettings(): CriticalSoundSettings {
  return useCriticalSoundSettingsState().settings
}
